import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Button, InputAdornment, TextField } from '@mui/material';
import clsx from 'clsx';
import { FC, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from 'src/requests/oidc';
import { validatePassword } from '../../helpers';
import {
  useGetRuleValidationsByFieldNameQuery,
  useGetSettingsQuery,
} from '../../redux/services/settings';
import { useChangePasswordMutation } from '../../redux/services/user';
import { RootState } from '../../redux/store';
import { checkCredentials } from '../../requests/user';
import { CustomTypography } from '../custom/CustomTypography';
import { IconWithTooltip } from '../shared/IconWithTooltip';
import styles from './ChangePassword.module.css';

const mapStateToProps = (state: RootState) => ({
  login: state.user.profile.login,
  userId: state.user.profile.id,
  passwordChangeRequired: state.user.profile.password_change_required,
});

type TChangePassword = {
  login?: string;
  userId?: string;
  passwordChangeRequired?: boolean;
};

export const ChangePasswordComponent: FC<TChangePassword> = ({
  login = '',
  userId,
  passwordChangeRequired,
}) => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState<null | string>(null);
  const [currentPasswordError, setCurrentPasswordError] = useState<null | string>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changePassword] = useChangePasswordMutation();
  const { data: rules } = useGetRuleValidationsByFieldNameQuery('password');
  const { data: dataSettings } = useGetSettingsQuery();

  const getCurrentPasswordError = () => {
    if (!currentPassword) return setCurrentPasswordError('Укажите текущий пароль');
    if (newPassword === currentPassword) {
      return setNewPasswordError('Текущий и новый пароль не должны совпадать');
    }
  };

  const getNewPasswordError = () => {
    if (!newPassword) return setNewPasswordError('Укажите новый пароль');
    const validationError = validatePassword(newPassword, dataSettings);
    if (validationError) {
      return setNewPasswordError(validationError);
    }
    if (newPassword === currentPassword) {
      return setNewPasswordError('Текущий и новый пароль не должны совпадать');
    }
  };

  const handleChangePassword = async () => {
    if (!currentPasswordError && !newPasswordError && newPassword && userId) {
      const verified = currentPassword && (await checkCredentials(login, currentPassword));

      if (verified || passwordChangeRequired) {
        await changePassword({ newPassword, userId, revokeTokens: true });
        logout();
      } else if (verified === false) {
        setCurrentPasswordError('Неправильный текущий пароль');
      }
    }
  };
  return (
    <div className={'wrapper-scroll'}>
      <div className={'content'}>
        <div className={styles['change-password']}>
          <CustomTypography
            style={{ marginBottom: 24 }}
            className={clsx('text-24-medium', 'font-golos')}
          >
            Изменить пароль
          </CustomTypography>
          <CustomTypography
            color="secondary"
            style={{ marginBottom: 24 }}
            className={clsx('text-14')}
          >
            После смены пароля произойдет выход из аккаунта на всех устройствах, где вы вошли с
            текущим паролем.
          </CustomTypography>
          {userId && (
            <>
              {!passwordChangeRequired && (
                <>
                  <CustomTypography style={{ marginBottom: 8 }} className={clsx('text-14')}>
                    Текущий пароль
                  </CustomTypography>
                  <TextField
                    value={currentPassword}
                    onChange={(e) => {
                      if (currentPasswordError) setCurrentPasswordError(null);
                      setCurrentPassword(e.target.value);
                    }}
                    error={!!currentPasswordError}
                    helperText={currentPasswordError ? currentPasswordError : ''}
                    onBlur={getCurrentPasswordError}
                    fullWidth
                    variant="standard"
                    className={clsx('custom', styles['password-textfield'])}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="off"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconWithTooltip
                              dataAttribute="show-secret-key"
                              muiIcon={
                                showPassword ? VisibilityOffOutlinedIcon : VisibilityOutlinedIcon
                              }
                              customTitle={showPassword ? 'Скрыть' : 'Показать'}
                              onClick={() => {
                                setShowPassword((show) => !show);
                              }}
                            />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </>
              )}
              <CustomTypography style={{ marginBottom: 8 }} className={clsx('text-14')}>
                Новый пароль
              </CustomTypography>
              <TextField
                value={newPassword}
                onChange={(e) => {
                  if (newPasswordError) setNewPasswordError(null);
                  setNewPassword(e.target.value);
                }}
                error={!!newPasswordError}
                helperText={newPasswordError ? newPasswordError : ''}
                onBlur={getNewPasswordError}
                fullWidth
                variant="standard"
                className={clsx('custom', styles['password-textfield'])}
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="off"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconWithTooltip
                          dataAttribute="show-secret-key"
                          muiIcon={
                            showNewPassword ? VisibilityOffOutlinedIcon : VisibilityOutlinedIcon
                          }
                          customTitle={showNewPassword ? 'Скрыть' : 'Показать'}
                          onClick={() => {
                            setShowNewPassword((show) => !show);
                          }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <CustomTypography
                color="secondary"
                style={{ marginBottom: 24 }}
                className={clsx('text-14')}
              >
                {rules && rules.length > 0 ? (
                  <ul>
                    {rules.map((rule) => (
                      <li key={rule.id}>{rule.title}</li>
                    ))}
                  </ul>
                ) : (
                  'Нет правил для пароля'
                )}
              </CustomTypography>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 48 }}>
                <Button onClick={() => navigate(-1)} variant="custom" color="secondary">
                  Отмена
                </Button>
                <Button onClick={handleChangePassword} variant="custom" style={{ marginLeft: 24 }}>
                  Изменить
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const ChangePassword = connect(mapStateToProps)(ChangePasswordComponent);
